/* eslint-disable */
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Route } from 'react-router-dom';
import { AllieUnpluggedIcon } from '../../../styling/icons';

interface RoutePropsV6 {
  path?: string;
  element?: React.ReactNode;
  index?: boolean;
}

// Error Boundary State
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

// Error messages in different languages
const ERROR_MESSAGES = {
  ES: {
    CONNECTION_ERROR: {
      title: 'No se pudo completar la solicitud',
      description: 'Ocurrió un error al generar la respuesta. Intenta de nuevo en unos minutos o contacta a soporte si el problema continúa.',
    },
    UNEXPECTED_ERROR: {
      title: 'No se pudo completar la solicitud',
      description: 'Ocurrió un error al generar la respuesta. Intenta de nuevo en unos minutos o contacta a soporte si el problema continúa.',
    },
    REQUEST_ERROR: {
      title: 'No se pudo completar la solicitud',
      description: 'Ocurrió un error al generar la respuesta. Intenta de nuevo en unos minutos o contacta a soporte si el problema continúa.',
    },
    TECHNICAL_DETAILS: '🔧 Detalles técnicos del error (Solo desarrollo)',
  },
  EN: {
    CONNECTION_ERROR: {
      title: 'Request could not be completed',
      description: 'An error occurred while generating the response. Try again in a few minutes or contact support if the problem continues.',
    },
    UNEXPECTED_ERROR: {
      title: 'Request could not be completed',
      description: 'An error occurred while generating the response. Try again in a few minutes or contact support if the problem continues.',
    },
    REQUEST_ERROR: {
      title: 'Request could not be completed',
      description: 'An error occurred while generating the response. Try again in a few minutes or contact support if the problem continues.',
    },
    TECHNICAL_DETAILS: '🔧 Technical error details (Development only)',
  },
};

// Error Fallback Component
const AllieErrorFallback: React.FC<{ error?: Error | null; language?: string }> = ({ 
  error, 
  language = 'ES' 
}) => {
  const messages = ERROR_MESSAGES[language as keyof typeof ERROR_MESSAGES];
  
  const getErrorMessage = () => {
    if (error && error.message.includes('Failed to fetch')) {
      return messages.CONNECTION_ERROR;
    }

    if (error) {
      return messages.UNEXPECTED_ERROR;
    }

    return messages.REQUEST_ERROR;
  };

  const { title, description } = getErrorMessage();

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '60vh',
      padding: '40px 20px',
      backgroundColor: '#f8f9fa',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    }}>
      {/* Icon - AllieUnpluggedIcon */}
      <div style={{
        width: '120px',
        height: '120px',
        marginBottom: '32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'transparent'
      }}>
        <AllieUnpluggedIcon />
      </div>

      {/* Title */}
      <h1 style={{
        fontSize: '28px',
        fontWeight: '600',
        color: '#212529',
        marginBottom: '16px',
        textAlign: 'center',
        maxWidth: '600px',
        lineHeight: '1.3'
      }}>
        {title}
      </h1>

      {/* Description */}
      <div style={{
        textAlign: 'center',
        maxWidth: '480px',
        marginBottom: '0',
        lineHeight: '1.6'
      }}>
        <p style={{
          fontSize: '16px',
          color: '#6c757d',
          margin: '0',
          lineHeight: '1.6'
        }}>
          {description}
        </p>
      </div>

      {/* Development error details */}
      {process.env.NODE_ENV === 'development' && error && (
        <details style={{
          marginTop: '40px',
          textAlign: 'left',
          maxWidth: '800px',
          width: '100%',
          padding: '20px',
          backgroundColor: '#fff',
          borderRadius: '8px',
          fontSize: '13px',
          color: '#666',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          border: '1px solid #e9ecef'
        }}>
          <summary style={{
            cursor: 'pointer',
            fontWeight: '600',
            marginBottom: '16px',
            color: '#495057',
            fontSize: '14px'
          }}>
            {messages.TECHNICAL_DETAILS}
          </summary>
          <pre style={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontSize: '12px',
            lineHeight: '1.5',
            backgroundColor: '#f8f9fa',
            padding: '16px',
            borderRadius: '6px',
            border: '1px solid #dee2e6',
            margin: '0',
            fontFamily: 'Monaco, Consolas, "Liberation Mono", "Courier New", monospace'
          }}>
            <strong style={{ color: '#dc3545' }}>Error:</strong> {error.message}
            {error.stack && (
              <>
                {'\n\n'}
                <strong style={{ color: '#dc3545' }}>Stack:</strong> {error.stack}
              </>
            )}
          </pre>
        </details>
      )}
    </div>
  );
};

// Error Boundary Component
export class RouteErrorBoundary extends Component<
  { children: ReactNode; onError?: (error: Error, errorInfo: ErrorInfo) => void; language?: string },
  ErrorBoundaryState
> {
  constructor(props: { children: ReactNode; onError?: (error: Error, errorInfo: ErrorInfo) => void; language?: string }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('AllieRoute Error:', error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <AllieErrorFallback error={this.state.error} language={this.props.language} />;
    }

    return this.props.children;
  }
}

// AllieRoute Props for React Router v6 only
export interface AllieRouteProps extends RoutePropsV6 {
  // Legacy support for component and render props (converted to element)
  component?: React.ComponentType<any>;
  render?: (props: any) => React.ReactNode;
  children?: React.ReactNode;
  
  // Additional props for AllieRoute
  showErrorDetails?: boolean;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  language?: string;
}

/**
 * AllieRoute - Enhanced Route component with built-in error handling
 * 
 * This component wraps React Router's Route with automatic error boundary
 * functionality, providing consistent error UI across the entire application.
 * 
 * Features:
 * - Automatic error boundary with consistent UI design
 * - Compatible with React Router v5 and v6
 * - Multi-language support (Spanish and English)
 * - Development mode error details
 * - Consistent styling matching the application design system
 * 
 * @param props - Standard RouteProps plus additional AllieRoute options
 * @param props.language - Language for error messages ('ES' | 'EN'), defaults to 'ES'
 */
/**
 * Hook to create an element wrapped with error boundary for React Router v6
 * 
 * @param element - The element to wrap
 * @param options - Error boundary options
 * @returns The wrapped element ready to use with Route
 */
export const useAllieRouteElement = (
  element: React.ReactNode,
  options: {
    onError?: (error: Error, errorInfo: ErrorInfo) => void;
    language?: string;
  } = {}
) => {
  const { onError, language = 'ES' } = options;
  
  return (
    <RouteErrorBoundary onError={onError} language={language}>
      {element}
    </RouteErrorBoundary>
  );
};

/**
 * AllieRoute - Enhanced Route component for React Router v6 with built-in error handling
 * 
 * DEPRECATED: Use regular Route with useAllieRouteElement hook instead
 * This component is kept for backward compatibility but may not work in all React Router v6 contexts
 */
export const AllieRoute: React.FC<AllieRouteProps> = ({ 
  onError,
  showErrorDetails = process.env.NODE_ENV === 'development',
  children,
  component,
  render,
  element,
  path,
  index,
  language = 'ES'
}) => {
  // Create the wrapped content with error boundary
  const wrappedElement = useAllieRouteElement(
    element || children || (component ? React.createElement(component, {}) : render?.({})),
    { onError, language }
  );

  // Return JSX Route directly for React Router v6 compatibility
  return (
    <Route
      path={path}
      index={index}
      element={wrappedElement}
    />
  );
};

AllieRoute.displayName = 'AllieRoute';